import { useQuery } from "@tanstack/react-query";
import { pageParams, scholarshipsApi } from "../../../api/scholarships";
import { getUserRole } from "../../../utils/auth";
import { SCHOLARSHIPS_KEY, isLoanType } from "../model";

// Every award after HEC's award list: the DG's and the Minister's approval (amounts for government loans only)

// A loan's award list goes to the DG and the Minister, award by award
export const loansOn = (scholarship) => Boolean(scholarship) && isLoanType(scholarship.type);

export const LOAN_STATUSES = [
  { value: "WITH_DG", label: "With the DG", color: "blue" },
  { value: "WITH_MINISTER", label: "With the Minister", color: "brand" },
  { value: "RETURNED_TO_HEC", label: "Returned to HEC", color: "yellow" },
  { value: "APPROVED", label: "Approved", color: "green" },
  { value: "REJECTED", label: "Rejected", color: "red" },
  { value: "RESERVE", label: "Reserve", color: "gray" },
];
// A sponsored scholarship's nominees: the DG signs the list off, then the sponsor grants or refuses each
export const NOMINEE_STATUSES = [
  { value: "WITH_DG", label: "With the DG", color: "blue" },
  { value: "WITH_SPONSOR", label: "With the partner", color: "brand" },
  { value: "GRANTED", label: "Granted by the partner", color: "green" },
  { value: "REJECTED", label: "Not granted by the partner", color: "red" },
  { value: "RESERVE", label: "Reserve", color: "gray" },
];
export const statusesFor = (isLoan) => (isLoan ? LOAN_STATUSES : NOMINEE_STATUSES);
export const loanStatusOf = (value, isLoan = true) => statusesFor(isLoan).find((s) => s.value === value) ?? { value, label: value ?? "—", color: "gray" };

// "Admin" is the administrator's former name, on loans rejected before the rename
const REJECTED_BY = { DG: "by the DG", MINISTER: "by the Minister", SCHOLARSHIP_ADMIN: "by HEC", Admin: "by HEC" };
export const rejectedByLabel = (role) => REJECTED_BY[role] ?? "";

/**
 * What each role may do in each status (the API applies the same rules). Labels are the buttons;
 * `reason` says whether the action needs one. The HEC Admin's loans go to the DG to sign off; the
 * DG's go straight to the Minister.
 */
export const ACTIONS = {
  DG: {
    WITH_DG: [
      { action: "APPROVE", label: "Approve and send to the Minister" },
      { action: "RETURN", label: "Return to HEC", reason: true },
      { action: "REJECT", label: "Reject", reason: true, destructive: true },
    ],
    RESERVE: [{ action: "PROMOTE", label: "Promote to the Minister" }],
    RETURNED_TO_HEC: [
      { action: "RESEND", label: "Send back to the Minister" },
      { action: "REJECT", label: "Reject", reason: true, destructive: true },
    ],
  },
  MINISTER: {
    WITH_MINISTER: [
      { action: "APPROVE", label: "Approve" },
      { action: "RETURN", label: "Return to the DG", reason: true },
      { action: "REJECT", label: "Reject", reason: true, destructive: true },
    ],
  },
  SCHOLARSHIP_ADMIN: {
    RESERVE: [{ action: "PROMOTE", label: "Promote and send to the DG" }],
    RETURNED_TO_HEC: [
      { action: "RESEND", label: "Send back to the DG" },
      { action: "REJECT", label: "Reject", reason: true, destructive: true },
    ],
  },
};
// What the DG sends to the Minister, with an optional note
export const SENDS_TO_MINISTER = ["APPROVE", "PROMOTE", "RESEND"];
export const sendsToMinister = (role, action) => role === "DG" && SENDS_TO_MINISTER.includes(action);
// Whoever holds a loan in each status may change its amounts
export const HOLDER = { WITH_DG: ["DG"], WITH_MINISTER: ["MINISTER"], RETURNED_TO_HEC: ["DG", "SCHOLARSHIP_ADMIN"] };

// The signed-in user's part in loan approvals: "DG", "MINISTER", "SCHOLARSHIP_ADMIN", or null
export const myLoanRole = () => (ACTIONS[getUserRole()] ? getUserRole() : null);
export const actionsFor = (role, status) => ACTIONS[role]?.[status] ?? [];

// Who acts next on each status, for the list counters of each role
export const MY_QUEUE = { DG: ["WITH_DG", "RETURNED_TO_HEC"], MINISTER: ["WITH_MINISTER"], SCHOLARSHIP_ADMIN: ["RETURNED_TO_HEC"] };

export const LOANS_KEY = [...SCHOLARSHIPS_KEY, "loans"];
export const loansKey = (scholarshipId) => [...LOANS_KEY, scholarshipId];

// Sponsored scholarships whose nomination list HEC sent to the DG, the latest first
export const useNominations = () =>
  useQuery({
    queryKey: [...LOANS_KEY, "nominations"],
    queryFn: async () => {
      const page = (await scholarshipsApi.get("staff/scholarships", { params: pageParams({ type: "COOPERATION", size: 100 }) })).data;
      return (page?.content ?? []).filter((s) => s.sentToDgAt).sort((a, b) => String(b.sentToDgAt).localeCompare(String(a.sentToDgAt)));
    },
  });

// The scholarship's loans and reserves, best HEC rank first
export const useLoans = (scholarshipId) =>
  useQuery({
    queryKey: loansKey(scholarshipId),
    queryFn: async () => (await scholarshipsApi.get(`staff/scholarships/${scholarshipId}/loans`)).data,
  });
